/**
 * AssignmentsSection
 *
 * Per-learner assignment list, split into Current vs Completed tabs.
 * Each row visualizes progress so admins can spot stalled learners at
 * a glance:
 *   • Course title (clickable → course page)
 *   • Type chip (Course / Learning Path / Event)
 *   • Progress bar + percentage
 *   • Deadline (highlighted red when overdue)
 *   • Status chip (Upcoming / Active / Overdue / Completed)
 *
 * Data source: `userAssignmentsClient.searchAllUserAssignments` — we
 * fetch all of this learner's assignments in a single shot (a learner
 * has tens, not thousands) and split client-side. The API accepts
 * `assignmentStatus` as a single value, so a server-side split would
 * require two calls without a meaningful UX win.
 */

import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AssignmentStatus, CourseEnrollment } from '../../apis/api-client';
import { userAssignmentsClient } from '../../apis/client-instances';
import {
  Box,
  Chip,
  CircularProgress,
  LinearProgress,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '../ui';
import { EmptyState } from '../common';
import {
  BookmarkIcon,
  CheckCircleIcon,
  PendingIcon,
  PlayCircleIcon,
  WarningIcon,
} from '../icons';
import { courseTypeMeta } from '../courses/contentTypeMeta';
import { alert } from '../../hooks/function';
import { formatDate, formatDateTime } from '../../utils/roleHelpers';

export interface AssignmentsSectionProps {
  userId: number;
}

// Type-coded icon + accent color for the course type pill. Same palette
// used everywhere content surfaces (curriculum builder, create-menu,
// provisioned-content list, etc.) so a Learning Path row reads the same
// here as it does elsewhere.

// Assignment-status visuals — small icon + label + soft palette. Lines
// up with the pastel status pills used on the Users table.
function assignmentStatusMeta(status: AssignmentStatus | undefined): {
  label: string;
  icon: React.ReactNode;
  bgcolor: string;
  color: string;
} {
  switch (status) {
    case AssignmentStatus.Upcoming:
      return {
        label: 'Upcoming',
        icon: <PendingIcon sx={{ fontSize: 14 }} />,
        bgcolor: '#f1f3f5',
        color: '#5a5f66',
      };
    case AssignmentStatus.Active:
      return {
        label: 'Active',
        icon: <PlayCircleIcon sx={{ fontSize: 14 }} />,
        bgcolor: 'rgba(var(--brand-rgb), 0.18)',
        color: '#1565c0',
      };
    case AssignmentStatus.Overdue:
      return {
        label: 'Overdue',
        icon: <WarningIcon sx={{ fontSize: 14 }} />,
        bgcolor: 'error.softBg',
        color: 'error.softText',
      };
    case AssignmentStatus.Completed:
      return {
        label: 'Completed',
        icon: <CheckCircleIcon sx={{ fontSize: 14 }} />,
        bgcolor: '#e6f7ec',
        color: '#1e7a3a',
      };
    default:
      return {
        label: '—',
        icon: <PendingIcon sx={{ fontSize: 14 }} />,
        bgcolor: '#f1f3f5',
        color: '#5a5f66',
      };
  }
}

export default function AssignmentsSection({ userId }: AssignmentsSectionProps) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [assignments, setAssignments] = useState<CourseEnrollment[]>([]);
  // Per-courseId slug lookup for navigation. The assignment endpoint
  // returns courseId but the user-facing course routes use slugs.
  const [tab, setTab] = useState<'current' | 'completed'>('current');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        // Page size 200 is the practical cap for a single learner. If
        // any learner ever has more, the column UI starts to break down
        // anyway and a paginated experience would be the right move.
        const result = await userAssignmentsClient.searchAllUserAssignments(
          undefined,
          undefined,
          undefined,
          undefined,
          userId,
          undefined,
          1,
          200,
        );
        if (cancelled) return;
        setAssignments(result.items ?? []);
      } catch {
        if (!cancelled) {
          alert(enqueueSnackbar, 'Failed to load assignments', 'error');
          setAssignments([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [userId, enqueueSnackbar]);

  // Split the loaded list by tab — Completed is its own slice, the
  // rest (Upcoming/Active/Overdue) all count as Current.
  const { currentAssignments, completedAssignments } = useMemo(() => {
    const current: CourseEnrollment[] = [];
    const completed: CourseEnrollment[] = [];
    for (const a of assignments) {
      if (a.assignmentStatus === AssignmentStatus.Completed) completed.push(a);
      else current.push(a);
    }
    return { currentAssignments: current, completedAssignments: completed };
  }, [assignments]);

  const rows = tab === 'current' ? currentAssignments : completedAssignments;

  const handleOpenCourse = (a: CourseEnrollment) => {
    if (!a.courseId) return;
    navigate(a.courseSlug ? `/courses/${a.courseSlug}` : `/course/${a.courseId}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Section header — title + summary counts. The counts sit
          inline so admins can scan totals without flipping tabs. */}
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" fontWeight={700}>
          Assignments
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {currentAssignments.length} current · {completedAssignments.length} completed
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tab} onChange={(_e, v) => setTab(v)}>
          <Tab
            value="current"
            label={`Current (${currentAssignments.length})`}
          />
          <Tab
            value="completed"
            label={`Completed (${completedAssignments.length})`}
          />
        </Tabs>
      </Box>

      {rows.length === 0 ? (
        <EmptyState
          icon={<BookmarkIcon sx={{ fontSize: 100, color: 'grey.400' }} />}
          title={tab === 'current' ? 'No current assignments' : 'No completed assignments yet'}
          subtitle={
            tab === 'current'
              ? 'Assign a course or learning path to see progress here.'
              : 'Completed assignments will appear here once this learner finishes assigned content.'
          }
        />
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          {rows.map((a) => {
            const typeMeta = courseTypeMeta((a as any).courseType);
            const statusMeta = assignmentStatusMeta(a.assignmentStatus);
            const progress = Math.round(Math.max(0, Math.min(100, a.progressPercentage ?? 0)));
            const overdue = a.assignmentStatus === AssignmentStatus.Overdue;
            const completed = a.assignmentStatus === AssignmentStatus.Completed;

            return (
              <Box
                key={`${a.courseId}-${a.enrollmentId ?? 'none'}`}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.25,
                  transition: 'border-color 120ms ease, box-shadow 120ms ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                  },
                }}
              >
                {/* Row 1: type chip pill icon + title + status chip */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1,
                      bgcolor: `${typeMeta.color}18`,
                      color: typeMeta.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {typeMeta.icon}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      noWrap
                      sx={{
                        cursor: a.courseId ? 'pointer' : 'default',
                        '&:hover': { color: a.courseId ? 'primary.main' : undefined },
                      }}
                      onClick={() => handleOpenCourse(a)}
                    >
                      {a.courseTitle || `Course #${a.courseId}`}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25 }}>
                      <Chip
                        label={typeMeta.label}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          bgcolor: `${typeMeta.color}15`,
                          color: typeMeta.color,
                          '& .MuiChip-label': { px: 0.75 },
                        }}
                      />
                    </Box>
                  </Box>
                  <Chip
                    icon={statusMeta.icon as any}
                    label={statusMeta.label}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      bgcolor: statusMeta.bgcolor,
                      color: statusMeta.color,
                      '& .MuiChip-label': { px: 0.75 },
                      '& .MuiChip-icon': { color: statusMeta.color, ml: 0.5 },
                    }}
                  />
                </Box>

                {/* Row 2: progress bar with inline percentage */}
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Progress
                    </Typography>
                    <Typography variant="caption" fontWeight={600} color={completed ? 'success.main' : 'primary.main'}>
                      {progress}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                      height: 6,
                      borderRadius: 999,
                      bgcolor: 'grey.100',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 999,
                        // Completed / overdue keep their solid status
                        // colors; in-progress picks up the brand gradient.
                        ...(completed
                          ? { bgcolor: 'success.main' }
                          : overdue
                            ? { bgcolor: 'error.main' }
                            : {
                                background:
                                  'var(--brand-gradient)',
                              }),
                      },
                    }}
                  />
                </Box>

                {/* Row 3: deadline + dates row */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    flexWrap: 'wrap',
                    color: 'text.secondary',
                  }}
                >
                  <Tooltip title="Started">
                    <Typography variant="caption">
                      <strong>Started:</strong> {formatDate(a.startDate) || '—'}
                    </Typography>
                  </Tooltip>
                  <Tooltip title="Deadline">
                    <Typography
                      variant="caption"
                      sx={{ color: overdue ? 'error.main' : 'inherit', fontWeight: overdue ? 600 : 'inherit' }}
                    >
                      <strong>Due:</strong> {formatDate(a.deadline) || '—'}
                    </Typography>
                  </Tooltip>
                  {completed && a.completedAt && (
                    <Typography variant="caption" sx={{ color: 'success.main' }}>
                      <strong>Completed:</strong> {formatDate(a.completedAt)}
                    </Typography>
                  )}
                  {!completed && (
                    <Typography variant="caption">
                      <strong>Last active:</strong> {formatDateTime(a.lastAccessedAt) || '—'}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
